import { useEffect } from "react";

import { Navigate, Outlet, Route, Routes } from "react-router-dom";

import { usePageTitle } from "@/atoms/page-title-atom";
import { FETCH_SETTINGS_DETAILS } from "@/queries";
import { PageContainer } from "@components/page-container";
import { LoadingSlide, TitleSlide } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import { H1 } from "@components/utilities";
import { LogOnMount } from "@components/v2/analytics";
import { BillingForm } from "@features/billing/components";
import { CustomMetricsProvider } from "@features/custom-metrics/hooks/custom-metrics-provider";
import { useNorthbeamQuery } from "@hooks/http";
import { type FetchSettingsDetails } from "@north-beam/nb-client-side";
import { EmailExclusions } from "@pages/email-exclusions/email-exclusions";
import { SubscriptionOrderTags } from "@pages/subscriptions-order-tags/subscription-order-tags";

import { DataImportSpreadsheets } from "../data-spreadsheets";
import { DiscountCodeMappingsContainer } from "../discount-code-mappings";

import { AccountManagerSectionV2 } from "./account-manager-v2";
import { AdditionalManagedDomainsInput } from "./additional-managed-domains-input";
import { Apex } from "./apex/apex";
import { ApiKeys } from "./api-keys";
import { ConfiguredCurrency } from "./configured-currency";
import { Connections } from "./connections";
import { Currency } from "./currency";
import { CustomAnnotations } from "./custom-annotations";
import DnsEntry from "./dns-entry";
import { EnquireLabsIntegration } from "./enquire-labs-integration";
import { GoogleAnalyticsParameterExclusions } from "./google-analytics-parameter-exclusions";
import { ManageExports } from "./manage-exports";
import { ModelSettings } from "./model-settings/model-settings";
import { NorthbeamLabs } from "./northbeam-labs";
import { NotificationSettingsContainer } from "./notifications";
import {
  MetaShopOrderTags,
  OrderTagExclusions,
  TikTokShopOrderTags,
} from "./order-tag-filters";
import { TrackingEvents } from "./pixel-diagnostics";
import { Profile } from "./profile";
import { SettingsNav, SettingsPath } from "./settings-nav";
import { SubscriptionInformation } from "./subscription-information";
import { TrackingScriptTags } from "./tracking-script-tags";
import { UserSettings } from "./user-settings";
import { UtmGuide } from "./utm-guide";

const SettingsLayout = () => (
  <div className="p-4 flex gap-4">
    <LogOnMount name="Visit Settings Page" />
    <div className="w-1/4">
      <SettingsNav />
    </div>
    <div className="w-3/4">
      <Outlet />
    </div>
  </div>
);

const SettingsRoutes = () => {
  const [, setPageTitle] = usePageTitle();
  useEffect(() => {
    setPageTitle("Settings");
  }, [setPageTitle]);

  const { user } = useUser();

  const { data, loading } = useNorthbeamQuery<FetchSettingsDetails>(
    FETCH_SETTINGS_DETAILS,
    {},
  );

  if (loading) {
    return <LoadingSlide />;
  }

  if (!data) {
    return (
      <TitleSlide>
        <p className="text-center">No data :(</p>
      </TitleSlide>
    );
  }
  const {
    me: { managerUsers, featureFlags, installationScripts },
  } = data;

  return (
    <Routes>
      <Route element={<SettingsLayout />}>
        <Route
          path="/"
          element={<Navigate to={SettingsPath.PROFILE} replace />}
        />
        <Route
          path={SettingsPath.PROFILE}
          element={
            <PageContainer>
              <Profile />
            </PageContainer>
          }
        />
        <Route
          path={SettingsPath.BILLING}
          element={
            <PageContainer>
              <H1>Billing</H1>
              <p>
                If you are managing the billing details for multiple accounts,
                please do not input your details here. Instead, reach out to{" "}
                <a href="mailto:success@northbeam.io">
                  our customer support team
                </a>
                .
              </p>
              <hr />
              <BillingForm />
              <SubscriptionInformation />
            </PageContainer>
          }
        />
        <Route
          path={SettingsPath.ACCOUNT_MANAGERS}
          element={
            <PageContainer>
              <H1>Account managers</H1>
              <hr />
              <AccountManagerSectionV2 managerUsers={managerUsers} />
            </PageContainer>
          }
        />
        <Route
          path={SettingsPath.EMAIL_NOTIFICATIONS}
          element={
            <PageContainer>
              <H1>Email notification settings</H1>
              <hr />
              <NotificationSettingsContainer />
            </PageContainer>
          }
        />
        {user.featureFlags.enableDataExportManagementPage && (
          <Route
            path={SettingsPath.MANAGE_EXPORTS}
            element={
              <PageContainer>
                <H1>Manage exports</H1>
                <hr />
                <CustomMetricsProvider>
                  <ManageExports />
                </CustomMetricsProvider>
              </PageContainer>
            }
          />
        )}
        {user.featureFlags.enableConfigurableCurrencies ? (
          <Route
            path={SettingsPath.CURRENCY}
            element={<ConfiguredCurrency />}
          />
        ) : (
          <Route
            path={SettingsPath.CURRENCY}
            element={
              <PageContainer>
                <H1>Currency</H1>
                <hr />
                <Currency />
              </PageContainer>
            }
          />
        )}
        <Route
          path={SettingsPath.NORTHBEAM_LABS}
          element={
            <PageContainer>
              <H1>Northbeam Labs</H1>
              <hr />
              <NorthbeamLabs />
            </PageContainer>
          }
        />
        {user.featureFlags.userSettingsMenuItem && (
          <Route
            path={SettingsPath.USER_SETTINGS}
            element={
              <PageContainer>
                <UserSettings />
              </PageContainer>
            }
          />
        )}
        {user.featureFlags.enableApexPage && (
          <Route
            path={SettingsPath.APEX}
            element={
              <PageContainer>
                <Apex />
              </PageContainer>
            }
          />
        )}
        {user.featureFlags.showProcessingSettings && (
          <Route
            path={SettingsPath.MODEL_SETTINGS}
            element={
              <PageContainer>
                <ModelSettings />
              </PageContainer>
            }
          />
        )}
        <Route
          path={SettingsPath.EXCLUDE_ORDERS}
          element={
            <PageContainer>
              <OrderTagExclusions />
            </PageContainer>
          }
        />
        {user.featureFlags.enableShopInclusionByOrderTag && (
          <>
            <Route
              path={SettingsPath.META_SHOPS_ORDER_TAGS}
              element={
                <PageContainer>
                  <MetaShopOrderTags />
                </PageContainer>
              }
            />
            <Route
              path={SettingsPath.TIK_TOK_SHOPS_ORDER_TAGS}
              element={
                <PageContainer>
                  <TikTokShopOrderTags />
                </PageContainer>
              }
            />
          </>
        )}
        <Route
          path={SettingsPath.DATA_SPREADSHEETS}
          element={
            <PageContainer>
              <DataImportSpreadsheets />
            </PageContainer>
          }
        />
        <Route
          path={SettingsPath.EMAIL_EXCLUSIONS}
          element={
            <PageContainer>
              <EmailExclusions />
            </PageContainer>
          }
        />
        {user.featureFlags.showSubscriptionOrderTags && (
          <Route
            path={SettingsPath.SUBSCRIPTION_ORDER_TAGS}
            element={
              <PageContainer>
                <SubscriptionOrderTags />
              </PageContainer>
            }
          />
        )}
        <Route
          path={SettingsPath.CUSTOM_ANNOTATIONS}
          element={
            <PageContainer>
              <CustomAnnotations />
            </PageContainer>
          }
        />
        <Route
          path={SettingsPath.TRACKING_SCRIPT_TAGS}
          element={
            <PageContainer>
              <TrackingScriptTags installationScripts={installationScripts} />
            </PageContainer>
          }
        />
        {user.featureFlags.pixelDiagnosticsPage && (
          <Route
            path={SettingsPath.TRACKING_EVENTS}
            element={
              <PageContainer>
                <TrackingEvents />
              </PageContainer>
            }
          />
        )}
        <Route path={SettingsPath.UTM_GUIDE} element={<UtmGuide />} />
        <Route
          path={SettingsPath.GA_PARAM_EXCLUSIONS}
          element={<GoogleAnalyticsParameterExclusions />}
        />
        <Route
          path={SettingsPath.DNS_ENTRY}
          element={
            <PageContainer>
              <H1>Setup DNS Entry</H1>
              <p>
                Setting up a DNS entry improves our ability to track your
                customers when they return to your website after their first
                visit.
              </p>
              <hr />
              <DnsEntry />
            </PageContainer>
          }
        />
        <Route
          path={SettingsPath.CONNECTIONS}
          element={
            <PageContainer>
              <Connections integrationDetails={data.integrationDetails} />
            </PageContainer>
          }
        />
        <Route
          path={SettingsPath.ADDITIONAL_MANAGED_DOMAINS}
          element={
            <PageContainer>
              <AdditionalManagedDomainsInput />
            </PageContainer>
          }
        />
        <Route
          path={SettingsPath.API_KEYS}
          element={
            <PageContainer>
              <H1>API Keys</H1>
              <hr />
              <ApiKeys />
            </PageContainer>
          }
        />
        {featureFlags.enquireLabsIntegration && (
          <Route
            path={SettingsPath.INTEGRATE_ENQUIRE_LABS}
            element={
              <PageContainer>
                <EnquireLabsIntegration />
              </PageContainer>
            }
          />
        )}
        {featureFlags.showDiscountCodeMappings && (
          <Route
            path={SettingsPath.DISCOUNT_CODE_MAPPINGS}
            element={
              <PageContainer>
                <DiscountCodeMappingsContainer />
              </PageContainer>
            }
          />
        )}
      </Route>
    </Routes>
  );
};

export default SettingsRoutes;
